import clsx from 'clsx';
import { observer } from 'mobx-react-lite';

import { useIsOutOfViewport } from 'src/shared/hooks/use-is-out-of-viewport';

import { ComparingWellRigOperationStage } from '../../../presets/comparing-drilling-wells-chart/entities';
import { LoadingRigOperations, WellRigOperationStage } from '../../../presets/drilling-wells-chart/entities';
import type { WellsViewSettingsProvider } from '../../../presets/drilling-wells-chart/wells-view-settings-provider';
import type { Viewport } from '../../../shared/viewport/viewport';
import { ChartTooltip } from '../../chart-tooltip/chart-tooltip';
import { DrillingStageWrapperCompact } from '../../drilling-step-wrapper';
import { EmptyBlockWrapper } from '../../empty-block-wrapper';

import styles from './data-items-empty.module.scss';
import { DrillingStageTooltipInner } from './drilling-stage-tooltip-inner';

interface Props {
  item: WellRigOperationStage | LoadingRigOperations;
  horizontalViewport: Viewport;
  verticalViewport: Viewport;
  viewProvider: WellsViewSettingsProvider;
  onWellStageClick(wellId: number): void;
}

export const ComparingWellsDataItem = observer(function ComparingWellsDataItem({
  item,
  verticalViewport,
  horizontalViewport,
  viewProvider,
  onWellStageClick,
}: Props) {
  const isOutOfViewport = useIsOutOfViewport(item, horizontalViewport);

  if (item instanceof LoadingRigOperations) {
    return (
      <EmptyBlockWrapper
        item={item}
        horizontalViewport={horizontalViewport}
        verticalViewport={verticalViewport}
        className={styles.loadingBlock}
      />
    );
  }

  if (item instanceof WellRigOperationStage) {
    const isChangedComparingStage = item instanceof ComparingWellRigOperationStage && item.hasChanges;

    if (item.parentWell) {
      const tooltipPlacement = isOutOfViewport ? 'rightTop' : 'top';

      return (
        <ChartTooltip
          content={<DrillingStageTooltipInner stage={item} viewProvider={viewProvider} />}
          placement={tooltipPlacement}
        >
          <DrillingStageWrapperCompact
            item={item}
            horizontalViewport={horizontalViewport}
            verticalViewport={verticalViewport}
            className={clsx(isChangedComparingStage && styles.cardComparing)}
            onClick={onWellStageClick}
          />
        </ChartTooltip>
      );
    } else {
      return (
        <DrillingStageWrapperCompact
          item={item}
          horizontalViewport={horizontalViewport}
          verticalViewport={verticalViewport}
          className={clsx(isChangedComparingStage && styles.cardComparing)}
          onClick={onWellStageClick}
        />
      );
    }
  }

  return null;
});
